import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { getInvites, sendInvites, deleteInvite, rescheduleMeeting, addInvitees } from '../services/api';
import { getSocket } from '../services/socket';

const getStatusIcon = (status) => {
  switch (status) {
    case 'accepted':
      return <CheckCircleIcon color="success" fontSize="small" />;
    case 'declined':
      return <CancelIcon color="error" fontSize="small" />;
    case 'sent':
      return <MailOutlineIcon color="info" fontSize="small" />;
    default:
      return <HourglassEmptyIcon color="disabled" fontSize="small" />;
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'accepted':
      return 'Accettato';
    case 'declined':
      return 'Rifiutato';
    case 'sent':
      return 'Inviato';
    default:
      return 'In attesa';
  }
};

export default function MeetingDetailsDialog({ open, onClose, meeting, token, onUpdate }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(false);

  // Editable fields
  const [title, setTitle] = useState('');
  const [scheduledAt, setScheduledAt] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [notifyInvitees, setNotifyInvitees] = useState(true);

  // New invitee
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (open && meeting) {
      setTitle(meeting.title || '');
      setScheduledAt(meeting.scheduledAt ? new Date(meeting.scheduledAt) : null);
      setCustomMessage(meeting.customMessage || '');
      loadInvites();
    }
  }, [open, meeting]);

  // Listen for real-time RSVP updates
  useEffect(() => {
    if (!open || !meeting) return;

    const socket = getSocket();
    if (!socket) return;

    const handleRsvpUpdate = (data) => {
      if (data.meetingId === meeting.id || data.meetingCode === meeting.code) {
        console.log('[MeetingDetails] RSVP update received:', data);
        setInvites(prev =>
          prev.map(invite =>
            invite.id === data.inviteId || invite.email === data.email
              ? { ...invite, status: data.status }
              : invite
          )
        );
      }
    };

    socket.on('rsvp:updated', handleRsvpUpdate);

    return () => {
      socket.off('rsvp:updated', handleRsvpUpdate);
    };
  }, [open, meeting]);

  const loadInvites = async () => {
    if (!meeting) return;
    setLoading(true);
    try {
      const response = await getInvites(meeting.code, token);
      setInvites(response.invites || []);
    } catch (err) {
      setError('Errore nel caricamento degli inviti');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/${meeting.code}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendInvite = async (inviteId) => {
    try {
      await sendInvites(meeting.code, [inviteId], token);
      setSuccess('Invito inviato!');
      setTimeout(() => setSuccess(null), 3000);
      loadInvites();
    } catch (err) {
      setError('Errore nell\'invio dell\'invito');
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await deleteInvite(meeting.code, inviteId, token);
      setInvites(invites.filter((i) => i.id !== inviteId));
    } catch (err) {
      setError('Errore nella rimozione dell\'invito');
    }
  };

  const handleAddInvitee = async () => {
    if (!newEmail.trim()) return;
    try {
      await addInvitees(
        meeting.code,
        [{ email: newEmail.trim(), name: newName.trim() || null }],
        true, // sendNow
        token
      );
      setNewEmail('');
      setNewName('');
      setSuccess('Invitato aggiunto e invito inviato!');
      setTimeout(() => setSuccess(null), 3000);

      // Reload invites and update parent
      const invitesResponse = await getInvites(meeting.code, token);
      const updatedInvites = invitesResponse.invites || [];
      setInvites(updatedInvites);

      if (onUpdate) {
        onUpdate({ ...meeting, invites: updatedInvites });
      }
    } catch (err) {
      setError('Errore nell\'aggiunta dell\'invitato');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await rescheduleMeeting(
        meeting.code,
        {
          title,
          scheduledAt: scheduledAt?.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          customMessage,
          notifyInvitees,
        },
        token
      );

      if (notifyInvitees && response.invitesSent > 0) {
        setSuccess(`Meeting aggiornato! ${response.invitesSent} invito/i rinviato/i.`);
      } else {
        setSuccess('Meeting aggiornato!');
      }
      setTimeout(() => setSuccess(null), 3000);

      // Reload invites first
      const invitesResponse = await getInvites(meeting.code, token);
      const updatedInvites = invitesResponse.invites || [];
      setInvites(updatedInvites);

      // Update parent with new meeting data including invites
      if (onUpdate) {
        onUpdate({ ...response.meeting, invites: updatedInvites });
      }
    } catch (err) {
      setError(err.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (!meeting) return null;

  const meetingUrl = `${window.location.origin}/${meeting.code}`;
  const acceptedCount = invites.filter((i) => i.status === 'accepted').length;
  const declinedCount = invites.filter((i) => i.status === 'declined').length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Gestione Meeting
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Meeting Link */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Link Meeting
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={meetingUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? 'Copiato!' : 'Copia link'}>
                    <IconButton onClick={handleCopyLink} edge="end">
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Editable fields */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Titolo"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Data e Ora"
              value={scheduledAt}
              onChange={setScheduledAt}
              slotProps={{
                textField: { fullWidth: true, sx: { mb: 2 } },
              }}
              ampm={false}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            label="Messaggio personalizzato"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            multiline
            rows={2}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Invitees section */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1">
              Invitati ({invites.length})
            </Typography>
            {invites.length > 0 && (
              <Box>
                <Chip
                  size="small"
                  icon={<CheckCircleIcon />}
                  label={acceptedCount}
                  color="success"
                  variant="outlined"
                  sx={{ mr: 0.5 }}
                />
                <Chip
                  size="small"
                  icon={<CancelIcon />}
                  label={declinedCount}
                  color="error"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {invites.length > 0 ? (
                <List dense>
                  {invites.map((invite) => (
                    <ListItem key={invite.id} sx={{ px: 0 }}>
                      <ListItemText
                        primary={invite.name || invite.email}
                        secondary={invite.name ? invite.email : null}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={getStatusLabel(invite.status)}>
                          {getStatusIcon(invite.status)}
                        </Tooltip>
                        <Tooltip title={invite.status === 'accepted' ? 'Già accettato' : 'Reinvia invito'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleSendInvite(invite.id)}
                              disabled={invite.status === 'accepted'}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Rimuovi">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteInvite(invite.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Nessun invitato
                </Typography>
              )}

              {/* Add new invitee */}
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label="Email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  sx={{ flex: 2 }}
                />
                <TextField
                  size="small"
                  label="Nome"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddInvitee}
                  disabled={!newEmail.trim()}
                >
                  Aggiungi
                </Button>
              </Box>
            </>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Notify option */}
        <FormControlLabel
          control={
            <Checkbox
              checked={notifyInvitees}
              onChange={(e) => setNotifyInvitees(e.target.checked)}
            />
          }
          label="Notifica invitati delle modifiche (reinvia inviti)"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Chiudi</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={20} /> : 'Salva Modifiche'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
